import { createContext, useContext, useState, useEffect } from 'react';

const PWAContext = createContext(null);

export function PWAProvider({ children }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [canInstall, setCanInstall] = useState(false);

  useEffect(() => {
    // Check if already installed (standalone mode)
    const standalone = window.matchMedia('(display-mode: standalone)').matches
      || window.navigator.standalone === true;
    setIsStandalone(standalone);

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // For iOS, can install if not standalone
    if (iOS && !standalone) {
      setCanInstall(true);
    }

    // The early listener in index.html may have already captured the
    // beforeinstallprompt event before this context mounted. Pick it up.
    if (window.__deferredInstallPrompt) {
      console.log('PWA: using install prompt captured before mount');
      setDeferredPrompt(window.__deferredInstallPrompt);
      setCanInstall(true);
    }

    // Re-broadcast from index.html when the event fires after mount, plus a
    // direct listener as a fallback if the early capture script is absent.
    const handleBeforeInstallPrompt = (e) => {
      console.log('PWA: beforeinstallprompt event fired');
      e.preventDefault();
      window.__deferredInstallPrompt = e;
      setDeferredPrompt(e);
      setCanInstall(true);
    };

    const handleInstallAvailable = () => {
      console.log('PWA: install prompt available (re-broadcast)');
      setDeferredPrompt(window.__deferredInstallPrompt);
      setCanInstall(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('pwa-install-available', handleInstallAvailable);

    // Listen for successful install
    const handleAppInstalled = () => {
      console.log('PWA: appinstalled event fired');
      setCanInstall(false);
      setDeferredPrompt(null);
      setIsStandalone(true);
      window.__deferredInstallPrompt = null;
      localStorage.removeItem('pwa-dismiss-count');
      localStorage.removeItem('pwa-visit-count');
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    // Debug log
    console.log('PWA Context initialized:', {
      standalone,
      iOS,
      userAgent: navigator.userAgent,
      canInstallInitial: iOS && !standalone
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('pwa-install-available', handleInstallAvailable);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  /**
   * Trigger the install prompt
   * Returns 'ios' if iOS (caller should show instructions dialog)
   * Returns 'accepted' or 'dismissed' for Chrome/Android
   * Returns null if can't install
   */
  const handleInstall = async () => {
    if (isIOS) {
      return 'ios';
    }

    if (!deferredPrompt) {
      return null;
    }

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      setCanInstall(false);
    }

    // The prompt can only be used once; discard it everywhere.
    window.__deferredInstallPrompt = null;
    setDeferredPrompt(null);
    return outcome;
  };

  /**
   * Track visit count (for showing banner on 2nd visit)
   */
  const trackVisit = () => {
    const visitCount = parseInt(localStorage.getItem('pwa-visit-count') || '0') + 1;
    localStorage.setItem('pwa-visit-count', visitCount.toString());
    return visitCount;
  };

  /**
   * Track dismissal count (for hiding banner after 2 dismissals)
   */
  const trackDismissal = () => {
    const dismissCount = parseInt(localStorage.getItem('pwa-dismiss-count') || '0') + 1;
    localStorage.setItem('pwa-dismiss-count', dismissCount.toString());
    return dismissCount;
  };

  /**
   * Get current visit count
   */
  const getVisitCount = () => {
    return parseInt(localStorage.getItem('pwa-visit-count') || '0');
  };

  /**
   * Get current dismissal count
   */
  const getDismissalCount = () => {
    return parseInt(localStorage.getItem('pwa-dismiss-count') || '0');
  };

  /**
   * Check if banner should be shown
   * Show on 2nd+ visit, max 2 dismissals, not installed, can install
   */
  const shouldShowBanner = () => {
    if (isStandalone || !canInstall) return false;

    const visitCount = getVisitCount();
    const dismissCount = getDismissalCount();

    return visitCount >= 2 && dismissCount < 2;
  };

  const value = {
    isIOS,
    isStandalone,
    canInstall,
    handleInstall,
    trackVisit,
    trackDismissal,
    getVisitCount,
    getDismissalCount,
    shouldShowBanner,
  };

  return <PWAContext.Provider value={value}>{children}</PWAContext.Provider>;
}

export function useInstallPWA() {
  const context = useContext(PWAContext);
  if (!context) {
    throw new Error('useInstallPWA must be used within PWAProvider');
  }
  return context;
}
